import React, { useState } from 'react';
import { ArrowLeft, PenTool, MessagesSquare, Sparkles, RotateCcw, Lightbulb, Image, Video, Share2 } from 'lucide-react';

const CreatingCustomizingViewsArticle = ({ onBack }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="max-w-4xl mx-auto p-6">
      <button 
        onClick={onBack}
        className="flex items-center text-gray-600 hover:text-black mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Help Center
      </button>

      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg mb-8">
        <div className="flex items-start">
          <PenTool className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0 mt-1" />
          <div>
            <h1 className="text-2xl font-bold mb-3">Creating and Customizing Views</h1>
            <p className="text-gray-700">
              Learn how to efficiently describe your desired UI, answer clarification questions, and iteratively 
              refine your generated views. This guide provides strategies for writing effective prompts, 
              maximizing the potential of the AI-powered design generation system, and enhancing your prototypes
              with images, videos, and other media assets.
            </p>
          </div>
        </div>
      </div>

      {!expanded ? (
        <div>
          <p className="text-gray-700 mb-6">
            Creating effective prototypes starts with clear descriptions and iterative refinement. 
            By learning how to communicate your design requirements effectively and use the revision 
            tools, you can quickly transform rough ideas into polished, functional prototypes with rich media
            assets like images and videos.
          </p>
          <button
            onClick={() => setExpanded(true)}
            className="text-black font-medium hover:underline"
          >
            Read more
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <PenTool className="w-5 h-5 mr-2 text-gray-700" />
              Writing Effective UI Descriptions
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                The quality of your prototype depends greatly on how well you describe your vision. Here are strategies for crafting clear UI descriptions:
              </p>
              <ul className="list-disc pl-5 space-y-3">
                <li>
                  <strong>Be specific about layout and structure</strong> - Clearly describe how elements should be arranged (e.g., "a navigation bar at the top, followed by a hero section with image and text side by side").
                </li>
                <li>
                  <strong>Describe UI components in detail</strong> - Specify the exact elements you want (e.g., "a search box with a magnifying glass icon" rather than just "a search feature").
                </li>
                <li>
                  <strong>Order elements from top to bottom</strong> - Describe your UI as users would view it, starting from the top of the page and working downward.
                </li>
                <li>
                  <strong>Include interactivity notes</strong> - Explain how users should interact with elements (e.g., "clicking the 'Read more' button expands the card to show additional text").
                </li>
                <li>
                  <strong>Specify content where important</strong> - Provide actual text for headings, buttons, and key content areas rather than relying on placeholders.
                </li>
                <li>
                  <strong>Consider media placement</strong> - If your design requires images or videos, specify their placement, aspect ratio, and content context (e.g., "product thumbnail image with 1:1 aspect ratio" or "tutorial video section with 16:9 aspect ratio").
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <MessagesSquare className="w-5 h-5 mr-2 text-gray-700" />
              Responding to Clarification Questions
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                After submitting your initial description, you'll receive clarification questions to refine your design. Here's how to respond effectively:
              </p>
              <ul className="list-disc pl-5 space-y-3">
                <li>
                  <strong>Be concise but thorough</strong> - Provide clear answers without unnecessary details.
                </li>
                <li>
                  <strong>Consider device constraints</strong> - If designing for mobile, remember space limitations when answering questions about layout.
                </li>
                <li>
                  <strong>Provide measurements when asked</strong> - For questions about sizing, use specific values (e.g., "the header should be 72px tall").
                </li>
                <li>
                  <strong>Reference familiar patterns</strong> - You can refer to common UI patterns (e.g., "similar to an e-commerce product card").
                </li>
                <li>
                  <strong>Focus on user experience</strong> - When asked about interactions, consider the user's context and needs.
                </li>
              </ul>
              <p className="mt-4">
                Remember that these questions help the AI understand your vision. More detailed answers lead to better results.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <Sparkles className="w-5 h-5 mr-2 text-gray-700" />
              Generating the Initial View
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                After answering clarification questions, you'll proceed to view generation. Here's what to expect:
              </p>
              <ol className="list-decimal pl-5 space-y-2">
                <li>
                  <strong>Processing time</strong> - Generation typically takes 15-30 seconds, depending on complexity.
                </li>
                <li>
                  <strong>Preview the result</strong> - You'll see your generated view displayed with all the elements and styling you specified.
                </li>
                <li>
                  <strong>Evaluate the output</strong> - Check if the generated view matches your requirements and expectations.
                </li>
                <li>
                  <strong>Save or revise</strong> - Either accept the view as is, or proceed to revisions if needed.
                </li>
              </ol>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <RotateCcw className="w-5 h-5 mr-2 text-gray-700" />
              Iteratively Refining Your Views
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                Most designs benefit from revision. Here's how to effectively refine your views:
              </p>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Click "Revise this version"</strong> on a generated view to begin refining.
                </li>
                <li>
                  <strong>Use specific revision categories</strong>:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li><strong>Replace</strong> - Swap out existing elements</li>
                    <li><strong>Add</strong> - Insert new elements at specific positions</li>
                    <li><strong>Remove</strong> - Delete unwanted elements</li>
                    <li><strong>Adjust</strong> - Change positioning, sizing, or spacing</li>
                    <li><strong>Refine</strong> - Make styling or content improvements</li>
                  </ul>
                </li>
                <li>
                  <strong>Be precise in your revisions</strong> - Describe exactly what needs to change (e.g., "increase the space between the heading and paragraph by 12px").
                </li>
                <li>
                  <strong>Submit multiple revisions if needed</strong> - Complex designs often require 2-3 rounds of revisions.
                </li>
                <li>
                  <strong>Compare versions</strong> - Use the version navigation to compare before and after revisions.
                </li>
              </ol>
              <p className="mt-4">
                Each revision creates a new version, allowing you to track changes and revert if needed.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <Image className="w-5 h-5 mr-2 text-gray-700" />
              Adding Images to Your Prototypes
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                Enhance your prototypes with real images to make them more visually engaging:
              </p>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Click "Add Media" from the view details page</strong> to see all available image placeholders.
                </li>
                <li>
                  <strong>Image placeholders</strong> are highlighted with yellow borders and numbered in the order they appear in your view.
                </li>
                <li>
                  <strong>Add images in two ways</strong>:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li><strong>Choose</strong> - Select from previously uploaded images across your projects</li>
                    <li><strong>Upload</strong> - Upload new JPG or WEBP images (max 2MB)</li>
                  </ul>
                </li>
                <li>
                  <strong>Pay attention to the suggested aspect ratio</strong> - The system recommends optimal image dimensions for each placeholder.
                </li>
                <li>
                  <strong>Save your changes</strong> - Click "Save" to apply all image selections to your view.
                </li>
              </ol>
              <p className="mt-4">
                Uploaded images are stored in your account and can be reused across different projects and views.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <Video className="w-5 h-5 mr-2 text-gray-700" />
              Embedding Videos in Your Prototypes
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                Add video content to create more dynamic and interactive prototypes:
              </p>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Video placeholders</strong> are highlighted with red borders and numbered in your view.
                </li>
                <li>
                  <strong>Click "Provide Video" on a placeholder</strong> to add video content.
                </li>
                <li>
                  <strong>Enter a URL from supported platforms</strong>:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>YouTube (youtube.com, youtu.be)</li>
                    <li>Vimeo (vimeo.com)</li>
                    <li>Dailymotion (dailymotion.com, dai.ly)</li>
                  </ul>
                </li>
                <li>
                  <strong>Videos are embedded responsively</strong> - They automatically adapt to different screen sizes while maintaining aspect ratio.
                </li>
              </ol>
              <p className="mt-4">
                Video embeds are ideal for tutorials, demonstrations, background videos, and product showcases.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <Share2 className="w-5 h-5 mr-2 text-gray-700" />
              Sharing Your Prototypes
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                Share your designs with clients, team members, or stakeholders:
              </p>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Create share links</strong> for individual views or complete flows.
                </li>
                <li>
                  <strong>Set additional security</strong>:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Add password protection for sensitive designs</li>
                    <li>Set expiration dates for temporary access</li>
                  </ul>
                </li>
                <li>
                  <strong>Share specific versions</strong> - For views with multiple iterations, you can choose to share a specific version.
                </li>
                <li>
                  <strong>View limits</strong> - Each share link has a maximum view limit (200 views) for security purposes.
                </li>
                <li>
                  <strong>Manage all share links</strong> from your dashboard, where you can also revoke access if needed.
                </li>
              </ol>
              <p className="mt-4">
                Recipients receive a clean, focused view of your prototype without editing capabilities.
              </p>
            </div>
          </section>

          <section className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <Lightbulb className="w-5 h-5 mr-2 text-yellow-500" />
              Pro Tips for Better Results
            </h2>
            <ul className="list-disc pl-5 space-y-3 text-gray-700">
              <li><strong>Use industry-standard terminology</strong> - Refer to UI elements by their common names (e.g., "hamburger menu" or "carousel").</li>
              <li><strong>Break complex pages into sections</strong> - Describe one major section at a time from top to bottom.</li>
              <li><strong>Consider mobile and desktop differences</strong> - If targeting both, specify how layouts should adapt.</li>
              <li><strong>Focus on one revision type at a time</strong> - When refining, address layout issues before styling details.</li>
              <li><strong>Save multiple versions</strong> - Create alternative designs to compare different approaches.</li>
              <li><strong>Prepare images in advance</strong> - Use the recommended aspect ratios for best results.</li>
              <li><strong>Organize views into flows</strong> - Create complete user journeys by connecting related views.</li>
              <li><strong>Use "different model" option</strong> - If you're not satisfied with revisions, try the "Use a different model" option for fresh perspectives.</li>
            </ul>
          </section>

          <button
            onClick={() => setExpanded(false)}
            className="text-black font-medium hover:underline mt-4 block"
          >
            Show less
          </button>
        </div>
      )}
    </div>
  );
};

export default CreatingCustomizingViewsArticle;